// src/pages/IncomeReportPage.js
//
// Income Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents company income for a selected period using the current
// interim business logic:
//
//   NET INCOME =
//     realized visits in period
//     + voucher sales in period
//     + package sales in period
//     - voucher redeems in period
//     - package redeems in period
//
// WHY THIS REPORT EXISTS
// ----------------------
// At the moment the application does NOT yet have a strict 1:1 technical link
// between each voucher/package redeem and a specific visit.
//
// Because of that, the report currently works at a PERIOD / ACCOUNTING level,
// not at a perfect voucher-to-visit analytical level.
//
// This page is designed to be:
// - clear for business users,
// - consistent with the current app UI,
// - structurally similar to CommissionReportPage,
// - future-ready for more detailed backend payloads later.
//
// CURRENT BACKEND CONTRACT (TRANSITIONAL / BACKWARD-COMPATIBLE)
// -------------------------------------------------------------
// The backend may currently return combined voucher-domain blocks, for example:
//
// {
//   period: { from: "2026-04-01", to: "2026-04-30" },
//   currency: "PLN",
//
//   totals: {
//     visits_income: "2500.00",
//     voucher_sales_income: "1200.00",
//     package_sales_income: "400.00",
//     voucher_redeems_correction: "300.00",
//     package_redeems_correction: "50.00",
//     gross_before_correction: "4100.00",
//     net_income: "3750.00"
//   },
//
//   visits: [ ... ],
//   voucherSales: [ ... ],
//   packageSales: [ ... ],
//   voucherRedeems: [ ... ],
//   packageRedeems: [ ... ],
//
//   summary: { ... },
//   sections: {
//     visits: [ ... ],
//     voucher_sales: [ ... ],
//     package_sales: [ ... ],
//     corrections: [ ... ],
//     package_redeems: [ ... ]
//   }
// }
//
// IMPORTANT FRONTEND DESIGN
// -------------------------
// Even though the backend is now improved and explicitly returns category +
// businessMode on voucher-domain rows, this page still remains defensive.
//
// That means:
// - it prefers explicit backend split totals and explicit backend row metadata
// - but it still supports fallback splitting logic for transitional payloads
//
// BUSINESS MODE DETECTION RULES
// -----------------------------
// For voucher-domain rows we resolve family in this priority order:
//
//   1) explicit businessMode / business_mode
//   2) category === "Pakiet" or "package"
//   3) voucher number starts with "P" / "p"  -> package
//   4) otherwise                             -> voucher
//
// This means the page remains resilient if some older response shape still
// appears during deployment / migration.
//
// IMPORTANT UX DECISION
// ---------------------
// This page does NOT auto-load the report on first entry.
//
// Why?
// ----
// Because your preferred UX is explicit report generation:
//
//   - user selects / confirms period
//   - user clicks Generate
//   - only then the report is loaded
//
// This keeps the page aligned with your intended reporting workflow.
//
// PDF EXPORT
// ----------
// Similar to CommissionReportPage, we support PDF export with jsPDF + autoTable.
// The PDF export reflects the voucher/package split as well.
//
// ============================================================================

import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import AuthContext from "../../context/AuthContext";
import { fetchIncomeReport } from "../../services/reportService";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import "./IncomeReportPage.css";

/* -------------------------------------------------------------------------- */
/* Business mode constants                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Canonical business modes used by this report page.
 *
 * We intentionally keep them as plain strings:
 * - easy to inspect in logs/devtools,
 * - easy to transport in normalized rows,
 * - easy to compare in split helpers,
 * - consistent with the wider voucher/package split direction in the app.
 */
const REPORT_BUSINESS_MODES = Object.freeze({
  VOUCHER: "voucher",
  PACKAGE: "package",
});

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format any numeric value as money with 2 decimals.
 *
 * We intentionally keep this fixed-width and predictable:
 * - easier to scan in tables,
 * - easier to export to PDF,
 * - consistent with other report pages,
 * - avoids locale-specific surprises inside accounting-like tables.
 */
const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

/**
 * Best-effort table cell formatter for date/time values.
 *
 * Current backend often returns date strings already.
 * If the value is a string, we keep it untouched.
 * If it is Date-like, we format it.
 *
 * We intentionally keep it simple and readable instead of applying locale
 * formatting rules, because report tables benefit from stable, compact output.
 */
const formatDateTimeCell = (value) => {
  if (!value) return "—";

  if (typeof value === "string") return value;

  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "—";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

/* -------------------------------------------------------------------------- */
/* Safe data helpers                                                          */
/* -------------------------------------------------------------------------- */

const toArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const isDefined = (value) => value !== undefined && value !== null;

/**
 * Return the first value that is actually defined (not undefined / not null).
 *
 * Why this helper is useful here:
 * - backend response shapes are still in transition,
 * - the same logical value may exist under several alternative keys,
 * - we want one readable way to express:
 *     "take the first explicitly present value".
 */
const pickFirstDefined = (...values) => values.find(isDefined);

/**
 * Corrections should be displayed as positive "redeem values" in their own table,
 * because the UI itself communicates that this section is SUBTRACTED.
 *
 * So even if a backend row later sends a signed negative number,
 * the table still remains stable and readable.
 *
 * Example:
 * - backend might return: 100
 * - backend might later return: -100
 * - frontend correction table should still show: 100
 *
 * because subtraction is already expressed at the section / totals level.
 */
const toPositiveCorrection = (value) => {
  const n = toNumber(value, 0);
  return Math.abs(n);
};

/**
 * Build stable row ids for React table rendering.
 *
 * We prefer backend-provided identifiers when present,
 * but we still keep a fallback to avoid unstable list rendering if some rows
 * temporarily come from partial / transitional payloads.
 */
const buildRowId = (row, fallbackPrefix, index) => {
  return (
    row?.row_key ??
    row?.id ??
    row?.visit_id ??
    row?.redeem_id ??
    row?.voucher_id ??
    row?.order_id ??
    `${fallbackPrefix}-${index}`
  );
};

/**
 * De-duplicate normalized rows by their normalized `id`.
 *
 * Why this matters:
 * - during the migration phase the backend may temporarily expose:
 *     * a combined voucher-domain block
 *     * and an explicit package block
 * - if those blocks overlap, the frontend must not double count them
 *
 * The normalized `id` is the most practical dedupe anchor here.
 */
const dedupeRowsById = (rows) => {
  const map = new Map();

  toArray(rows).forEach((row) => {
    if (!row) return;
    map.set(String(row.id), row);
  });

  return Array.from(map.values());
};

/* -------------------------------------------------------------------------- */
/* Business mode detection helpers                                            */
/* -------------------------------------------------------------------------- */

/**
 * Normalize a raw business-mode-like value into one of:
 * - "voucher"
 * - "package"
 *
 * Unknown values return empty string so caller can continue with fallback logic.
 */
const normalizeBusinessModeValue = (value) => {
  const raw = String(value || "").trim().toLowerCase();

  if (raw === REPORT_BUSINESS_MODES.PACKAGE) {
    return REPORT_BUSINESS_MODES.PACKAGE;
  }

  if (raw === REPORT_BUSINESS_MODES.VOUCHER) {
    return REPORT_BUSINESS_MODES.VOUCHER;
  }

  return "";
};

/**
 * Check whether a voucher number looks like a package number.
 *
 * Current fallback business rule from migration:
 * - package-domain voucher rows are identified by voucher numbers
 *   starting with letter "P"
 *
 * IMPORTANT:
 * This is now only a defensive fallback.
 * The preferred source of truth is backend category / businessMode.
 */
const looksLikePackageVoucherNo = (value) => {
  const s = String(value || "").trim();
  if (!s) return false;
  return /^[Pp]/.test(s);
};

/**
 * Resolve business mode for any voucher-domain row used by this report.
 *
 * Resolution priority:
 *   1) explicit businessMode / business_mode
 *   2) category === "Pakiet" or "package"
 *   3) voucher number starts with P/p
 *   4) fallback => voucher
 *
 * This keeps the report resilient even if some older payload is still returned
 * during deployment / migration.
 */
const resolveVoucherBusinessModeFromRow = (row) => {
  const explicit =
    normalizeBusinessModeValue(row?.businessMode) ||
    normalizeBusinessModeValue(row?.business_mode);

  if (explicit) {
    return explicit;
  }

  const category = String(row?.category || "").trim().toLowerCase();
  if (category === "pakiet" || category === "package") {
    return REPORT_BUSINESS_MODES.PACKAGE;
  }

  const voucherNoCandidate =
    row?.voucher_no ??
    row?.voucherNo ??
    row?.voucher_search_no ??
    row?.voucherSearchNo ??
    "";

  if (looksLikePackageVoucherNo(voucherNoCandidate)) {
    return REPORT_BUSINESS_MODES.PACKAGE;
  }

  return REPORT_BUSINESS_MODES.VOUCHER;
};

/**
 * Split a normalized row list into voucher rows and package rows.
 *
 * The row objects must already contain:
 *   row.businessMode
 */
const splitRowsByBusinessMode = (rows) => {
  const voucherRows = [];
  const packageRows = [];

  toArray(rows).forEach((row) => {
    if (row?.businessMode === REPORT_BUSINESS_MODES.PACKAGE) {
      packageRows.push(row);
    } else {
      voucherRows.push(row);
    }
  });

  return { voucherRows, packageRows };
};

/* -------------------------------------------------------------------------- */
/* Response normalization                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Normalize visits into one stable frontend shape.
 *
 * This accepts both:
 * - older placeholder rows,
 * - current backend section rows.
 *
 * The goal is to keep JSX clean and make the page resilient while backend
 * payloads continue to evolve.
 */
const normalizeVisitRows = (rows) =>
  toArray(rows).map((row, index) => ({
    id: buildRowId(row, "visit", index),

    // Date fields can come in several names depending on backend version
    date:
      row?.date ??
      row?.occurred_at ??
      row?.date_time ??
      row?.realized_at ??
      row?.createdAt ??
      null,

    service: row?.service ?? row?.title ?? row?.category ?? "—",
    client: row?.client ?? row?.customer ?? row?.customer_name ?? "—",
    employee: row?.employee ?? row?.employee_name ?? row?.employee3P ?? "—",

    amount: toNumber(
      row?.amount ??
        row?.service_value ??
        row?.service_value3P ??
        row?.value ??
        row?.income,
      0
    ),
  }));

/**
 * Normalize voucher-domain SALES rows into one stable frontend shape.
 *
 * IMPORTANT
 * ---------
 * This function intentionally normalizes rows BEFORE the voucher/package
 * split is performed.
 *
 * Why?
 * ----
 * Because backend may send:
 * - one combined voucher-domain sales pool
 * - or explicit voucher sales + explicit package sales pools
 *
 * The report should work in both cases.
 *
 * Therefore this function:
 * - normalizes one generic sales-row shape,
 * - resolves businessMode for each row,
 * - lets a later split helper separate voucher vs package.
 */
const normalizeVoucherDomainSalesRows = (rows) =>
  toArray(rows).map((row, index) => {
    const amountFromMinorUnit =
      row?.amount_minor_unit !== null &&
      row?.amount_minor_unit !== undefined
        ? toNumber(row.amount_minor_unit, 0) / 100
        : null;

    const voucherNo =
      row?.voucher_no ??
      row?.voucherNo ??
      row?.voucher_search_no ??
      row?.voucherSearchNo ??
      "—";

    return {
      id: buildRowId(row, "voucher-sale", index),

      businessMode: resolveVoucherBusinessModeFromRow({
        ...row,
        voucher_no: voucherNo,
      }),

      date:
        row?.date ??
        row?.occurred_at ??
        row?.sold_at ??
        row?.paid_at ??
        row?.createdAt ??
        null,

      voucherNo,
      buyerName: row?.buyer_name ?? row?.buyerName ?? "—",
      recipientName: row?.recipient_name ?? row?.recipientName ?? "—",

      amount: toNumber(
        row?.amount ??
          row?.value ??
          amountFromMinorUnit ??
          row?.income,
        0
      ),

      status: row?.status ?? row?.payment_status ?? "—",
      category: row?.category ?? null,
    };
  });

/**
 * Normalize voucher-domain REDEEM rows into one stable frontend shape.
 *
 * IMPORTANT
 * ---------
 * This function also resolves businessMode per row, because package redeems
 * must no longer be hidden inside one generic voucher correction bucket.
 *
 * The table should display a POSITIVE redeem amount,
 * because subtraction is already expressed at the section / totals level.
 */
const normalizeVoucherDomainRedeemRows = (rows) =>
  toArray(rows).map((row, index) => {
    const voucherNo =
      row?.voucher_no ??
      row?.voucherNo ??
      row?.voucher_search_no ??
      row?.voucherSearchNo ??
      "—";

    return {
      id: buildRowId(row, "redeem", index),

      businessMode: resolveVoucherBusinessModeFromRow({
        ...row,
        voucher_no: voucherNo,
      }),

      date:
        row?.date ??
        row?.occurred_at ??
        row?.redeemed_at ??
        row?.createdAt ??
        null,

      voucherNo,
      reservationId: row?.reservation_id ?? row?.reservationId ?? "—",
      comment: row?.comment ?? row?.note ?? "—",

      amount: toPositiveCorrection(
        row?.amount ??
          row?.redeemed_amount ??
          row?.value ??
          row?.signed_amount ??
          row?.correction
      ),

      category: row?.category ?? null,
    };
  });

/**
 * Sum a numeric field in a row array.
 */
const sumField = (rows, field) =>
  toArray(rows).reduce((acc, row) => acc + toNumber(row?.[field], 0), 0);

/**
 * Resolve sales rows from a backend response and split them into:
 * - voucherSales
 * - packageSales
 *
 * Supported response shapes:
 * - legacy combined sales section only
 * - explicit voucher sales + explicit package sales
 * - mixed transitional payloads
 */
const resolveSalesRowsFromResponse = (response) => {
  const voucherSalesSection = pickFirstDefined(
    response?.voucherSales,
    response?.voucher_sales,
    response?.sections?.voucherSales,
    response?.sections?.voucher_sales,
    response?.data?.voucherSales,
    response?.data?.voucher_sales
  );

  const packageSalesSection = pickFirstDefined(
    response?.packageSales,
    response?.package_sales,
    response?.sections?.packageSales,
    response?.sections?.package_sales,
    response?.data?.packageSales,
    response?.data?.package_sales
  );

  const merged = dedupeRowsById([
    ...normalizeVoucherDomainSalesRows(voucherSalesSection),
    ...normalizeVoucherDomainSalesRows(packageSalesSection),
  ]);

  const { voucherRows, packageRows } = splitRowsByBusinessMode(merged);

  return {
    voucherSales: voucherRows,
    packageSales: packageRows,
  };
};

/**
 * Resolve redeem rows from a backend response and split them into:
 * - voucherRedeems
 * - packageRedeems
 *
 * Supported response shapes:
 * - legacy combined corrections section only
 * - explicit voucher redeems + explicit package redeems
 * - mixed transitional payloads
 */
const resolveRedeemRowsFromResponse = (response) => {
  const voucherRedeemsSection = pickFirstDefined(
    response?.voucherRedeems,
    response?.voucher_redeems,
    response?.redeems,
    response?.corrections,
    response?.sections?.voucherRedeems,
    response?.sections?.voucher_redeems,
    response?.sections?.redeems,
    response?.sections?.corrections,
    response?.data?.voucherRedeems,
    response?.data?.voucher_redeems
  );

  const packageRedeemsSection = pickFirstDefined(
    response?.packageRedeems,
    response?.package_redeems,
    response?.sections?.packageRedeems,
    response?.sections?.package_redeems,
    response?.data?.packageRedeems,
    response?.data?.package_redeems
  );

  const merged = dedupeRowsById([
    ...normalizeVoucherDomainRedeemRows(voucherRedeemsSection),
    ...normalizeVoucherDomainRedeemRows(packageRedeemsSection),
  ]);

  const { voucherRows, packageRows } = splitRowsByBusinessMode(merged);

  return {
    voucherRedeems: voucherRows,
    packageRedeems: packageRows,
  };
};

/**
 * Empty normalized report shape.
 *
 * Why keep this centralized?
 * - avoids repeated null guards in JSX,
 * - makes loading/error fallback predictable,
 * - provides one stable UI contract even when API fails,
 * - reduces accidental divergence between initial state and fallback state.
 */
const createEmptyReport = (from = "", to = "") => ({
  period: { from, to },
  currency: "PLN",

  visits: [],
  voucherSales: [],
  packageSales: [],
  voucherRedeems: [],
  packageRedeems: [],

  totals: {
    visitsIncome: 0,
    voucherSalesIncome: 0,
    packageSalesIncome: 0,
    voucherRedeemsCorrection: 0,
    packageRedeemsCorrection: 0,
    grossBeforeCorrection: 0,
    netIncome: 0,
  },
});

/**
 * Normalize many possible backend response shapes into one stable frontend model.
 *
 * Supported shapes include:
 * - flat array (legacy placeholder),
 * - current backend summary + sections shape,
 * - more future-oriented totals / sections variants.
 *
 * This function is intentionally defensive because the report is still evolving
 * together with the backend.
 *
 * HIGH-LEVEL POLICY
 * -----------------
 * 1) Visits:
 *    - straightforward: explicit total preferred, row sum as fallback
 *
 * 2) Voucher/package sales:
 *    - if backend already provides explicit split totals, use them
 *    - otherwise compute split totals from normalized/split rows
 *    - if there are no rows at all, fall back to legacy combined total
 *
 * 3) Voucher/package redeems:
 *    - same logic as sales
 *
 * 4) Gross / Net:
 *    - intentionally computed from the DISPLAYED split totals
 *    - this guarantees that Current report equation, summary cards,
 *      grand totals and section subtotals all reconcile
 */
const normalizeIncomeReportResponse = (response, fallbackFrom, fallbackTo) => {
  // ------------------------------------------------------------------------
  // Legacy placeholder:
  // if backend returns a plain array, treat it as visits only
  // ------------------------------------------------------------------------
  if (Array.isArray(response)) {
    const visits = normalizeVisitRows(response);
    const visitsIncome = sumField(visits, "amount");

    return {
      period: { from: fallbackFrom, to: fallbackTo },
      currency: "PLN",

      visits,
      voucherSales: [],
      packageSales: [],
      voucherRedeems: [],
      packageRedeems: [],

      totals: {
        visitsIncome,
        voucherSalesIncome: 0,
        packageSalesIncome: 0,
        voucherRedeemsCorrection: 0,
        packageRedeemsCorrection: 0,
        grossBeforeCorrection: visitsIncome,
        netIncome: visitsIncome,
      },
    };
  }

  // ------------------------------------------------------------------------
  // Detail rows
  // ------------------------------------------------------------------------
  const visits = normalizeVisitRows(
    response?.visits ??
      response?.sections?.visits ??
      response?.data?.visits
  );

  const { voucherSales, packageSales } = resolveSalesRowsFromResponse(response);
  const { voucherRedeems, packageRedeems } = resolveRedeemRowsFromResponse(response);

  // ------------------------------------------------------------------------
  // Computed row subtotals
  // ------------------------------------------------------------------------
  const computedVisitsIncome = sumField(visits, "amount");
  const computedVoucherSalesIncome = sumField(voucherSales, "amount");
  const computedPackageSalesIncome = sumField(packageSales, "amount");
  const computedVoucherRedeemsCorrection = sumField(voucherRedeems, "amount");
  const computedPackageRedeemsCorrection = sumField(packageRedeems, "amount");

  // ------------------------------------------------------------------------
  // Explicit totals from backend (when available)
  // ------------------------------------------------------------------------
  const explicitVisitsIncome = pickFirstDefined(
    response?.summary?.visits_total,
    response?.summary?.visits_income,
    response?.totals?.visits_income,
    response?.totals?.visitsIncome
  );

  /**
   * Backend now explicitly returns split totals.
   * We still keep fallback support in case an older payload appears.
   */
  const explicitVoucherSalesIncome = pickFirstDefined(
    response?.summary?.voucher_sales_total,
    response?.summary?.voucher_sales_income,
    response?.totals?.voucher_sales_income,
    response?.totals?.voucherSalesIncome
  );

  const explicitPackageSalesIncome = pickFirstDefined(
    response?.summary?.package_sales_total,
    response?.summary?.package_sales_income,
    response?.totals?.package_sales_income,
    response?.totals?.packageSalesIncome
  );

  const explicitVoucherRedeemsCorrection = pickFirstDefined(
    response?.summary?.voucher_redeems_total,
    response?.summary?.voucher_redeems_correction,
    response?.totals?.voucher_redeems_correction,
    response?.totals?.voucherRedeemsCorrection
  );

  const explicitPackageRedeemsCorrection = pickFirstDefined(
    response?.summary?.package_redeems_total,
    response?.summary?.package_redeems_correction,
    response?.totals?.package_redeems_correction,
    response?.totals?.packageRedeemsCorrection
  );

  // ------------------------------------------------------------------------
  // Final split totals used by the UI
  // ------------------------------------------------------------------------
  const visitsIncome = toNumber(explicitVisitsIncome, computedVisitsIncome);

  let voucherSalesIncome = 0;
  let packageSalesIncome = 0;

  if (isDefined(explicitPackageSalesIncome)) {
    // Backend explicitly supports split sales totals.
    voucherSalesIncome = toNumber(
      explicitVoucherSalesIncome,
      computedVoucherSalesIncome
    );
    packageSalesIncome = toNumber(
      explicitPackageSalesIncome,
      computedPackageSalesIncome
    );
  } else if (voucherSales.length > 0 || packageSales.length > 0) {
    // Defensive fallback for transitional payloads.
    voucherSalesIncome = computedVoucherSalesIncome;
    packageSalesIncome = computedPackageSalesIncome;
  } else {
    // Absolute fallback: only legacy voucher-only total exists, no rows.
    voucherSalesIncome = toNumber(explicitVoucherSalesIncome, 0);
    packageSalesIncome = 0;
  }

  let voucherRedeemsCorrection = 0;
  let packageRedeemsCorrection = 0;

  if (isDefined(explicitPackageRedeemsCorrection)) {
    voucherRedeemsCorrection = toPositiveCorrection(
      isDefined(explicitVoucherRedeemsCorrection)
        ? explicitVoucherRedeemsCorrection
        : computedVoucherRedeemsCorrection
    );
    packageRedeemsCorrection = toPositiveCorrection(
      isDefined(explicitPackageRedeemsCorrection)
        ? explicitPackageRedeemsCorrection
        : computedPackageRedeemsCorrection
    );
  } else if (voucherRedeems.length > 0 || packageRedeems.length > 0) {
    // Defensive fallback for transitional payloads.
    voucherRedeemsCorrection = computedVoucherRedeemsCorrection;
    packageRedeemsCorrection = computedPackageRedeemsCorrection;
  } else {
    voucherRedeemsCorrection = toPositiveCorrection(
      explicitVoucherRedeemsCorrection
    );
    packageRedeemsCorrection = 0;
  }

  // ------------------------------------------------------------------------
  // Final displayed equation totals
  // ------------------------------------------------------------------------
  const grossBeforeCorrection =
    visitsIncome + voucherSalesIncome + packageSalesIncome;

  const netIncome =
    grossBeforeCorrection -
    voucherRedeemsCorrection -
    packageRedeemsCorrection;

  return {
    period: {
      from:
        response?.filters?.from ??
        response?.period?.from ??
        fallbackFrom,
      to:
        response?.filters?.to ??
        response?.period?.to ??
        fallbackTo,
    },
    currency: response?.currency ?? response?.meta?.currency ?? "PLN",

    visits,
    voucherSales,
    packageSales,
    voucherRedeems,
    packageRedeems,

    totals: {
      visitsIncome,
      voucherSalesIncome,
      packageSalesIncome,
      voucherRedeemsCorrection,
      packageRedeemsCorrection,
      grossBeforeCorrection,
      netIncome,
    },
  };
};

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

// i18n note
// ---------
// The report remains calculation- and backend-payload driven. The i18n layer
// only replaces user-facing labels, messages, and PDF captions. All business
// normalization helpers and row semantics stay intentionally unchanged.
const IncomeReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  /**
   * Pilot usage of the shared evaluation-period framework.
   *
   * IMPORTANT:
   * We pass allowedModes explicitly.
   *
   * Why this is good:
   * - the shared hook can support role-based restrictions for other pages,
   * - Income Report remains intentionally unrestricted,
   * - page behavior is explicit and future-safe,
   * - this page continues to serve as the reference / pilot implementation.
   */
  const currentRole = authState?.user?.role || "";
  const canExportPdf = currentRole === "superuser";

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ],
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [report, setReport] = useState(() =>
    createEmptyReport(selectedFrom, selectedTo)
  );

  /**
   * MANUAL LOAD UX
   * --------------
   * This page intentionally starts with no loaded report.
   *
   * Why:
   * - you explicitly do NOT want auto-load,
   * - report loading should happen only after clicking Generate,
   * - this keeps the page aligned with your preferred reporting workflow.
   */
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfOrientation, setPdfOrientation] = useState("p"); // "p" portrait, "l" landscape

  /**
   * One stable async loader used by:
   * - Generate button
   *
   * IMPORTANT:
   * This function is intentionally NOT auto-triggered on mount.
   *
   * We keep the API explicit:
   *   loadReportByRange({ from, to })
   *
   * rather than implicitly pulling values from component state.
   * This makes the function easier to reason about, easier to test mentally,
   * and easier to reuse later.
   */
  const loadReportByRange = useCallback(async ({ from, to }) => {
    if (!from || !to) {
      setError(t("reports:common.invalidEvaluationPeriod"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetchIncomeReport(from, to);
      const normalized = normalizeIncomeReportResponse(response, from, to);
      setReport(normalized);
      setHasGeneratedOnce(true);
    } catch (err) {
      console.error("[IncomeReport] ❌ Failed to fetch income report:", err);
      setError(t("reports:income.fetchError"));
      setReport(createEmptyReport(from, to));
      setHasGeneratedOnce(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Whether there is any visible report data.
   *
   * This drives:
   * - {t("reports:common.exportPdf")} button visibility,
   * - empty-state rendering.
   *
   * We intentionally treat totals > 0 as meaningful data even if detail arrays
   * are empty, because backend may sometimes return summarized results first.
   */
  const hasReportData = useMemo(() => {
    return (
      report.visits.length > 0 ||
      report.voucherSales.length > 0 ||
      report.packageSales.length > 0 ||
      report.voucherRedeems.length > 0 ||
      report.packageRedeems.length > 0 ||
      report.totals.grossBeforeCorrection > 0 ||
      report.totals.netIncome > 0
    );
  }, [report]);

  /* ------------------------------------------------------------------------ */
  /* PDF export                                                               */
  /* ------------------------------------------------------------------------ */

  const generatePDF = () => {
    const doc = new jsPDF(pdfOrientation, "mm", "a4");
    const currency = report.currency || "PLN";

    // ----------------------------------------------------------------------
    // Header
    // ----------------------------------------------------------------------
    doc.setFontSize(16);
    doc.text(t("reports:income.pdfTitle"), 14, 12);

    doc.setFontSize(10);
    doc.text(
      t("reports:common.fromToPdfLine", { from: report.period.from || "N/A", to: report.period.to || "N/A" }),
      14,
      18
    );

    doc.setFontSize(10);
    doc.text(
      t("reports:income.businessRulePdf"),
      14,
      24
    );

    // ----------------------------------------------------------------------
    // Summary table
    // ----------------------------------------------------------------------
    autoTable(doc, {
      startY: 30,
      head: [[t("reports:common.metric"), t("reports:common.amount")]],
      body: [
        [t("reports:income.visitsIncome"), `${formatMoney(report.totals.visitsIncome)} ${currency}`],
        [t("reports:income.voucherSalesIncome"), `${formatMoney(report.totals.voucherSalesIncome)} ${currency}`],
        [t("reports:income.packageSalesIncome"), `${formatMoney(report.totals.packageSalesIncome)} ${currency}`],
        [t("reports:income.voucherRedeemsCorrection"), `- ${formatMoney(report.totals.voucherRedeemsCorrection)} ${currency}`],
        [t("reports:income.packageRedeemsCorrection"), `- ${formatMoney(report.totals.packageRedeemsCorrection)} ${currency}`],
        [t("reports:income.grossBeforeCorrection"), `${formatMoney(report.totals.grossBeforeCorrection)} ${currency}`],
        [t("reports:income.netIncome"), `${formatMoney(report.totals.netIncome)} ${currency}`],
      ],
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 14, right: 14 },
    });

    let cursorY = (doc.lastAutoTable?.finalY || 30) + 8;

    /**
     * Small layout helper:
     * creates a new page when the next block would not fit safely.
     */
    const ensurePageBreak = (extraHeightNeeded = 20) => {
      if (cursorY + extraHeightNeeded > 275) {
        doc.addPage();
        cursorY = 18;
      }
    };

    /**
     * Standardized section title renderer used by all report sections.
     */
    const addSectionTitle = (title, subtitle) => {
      ensurePageBreak(15);

      doc.setFontSize(12);
      doc.text(title, 14, cursorY);

      if (subtitle) {
        doc.setFontSize(9);
        doc.text(subtitle, 14, cursorY + 5);
      }

      cursorY += 8;
    };

    /**
     * Standardized section renderer for table-backed report blocks.
     *
     * Why keep one small helper here?
     * - all report sections now follow the same export pattern,
     * - the helper keeps export logic readable,
     * - section differences remain explicit in the call sites below.
     */
    const renderSectionTable = ({
      title,
      subtitle,
      head,
      body,
      emptyMessage,
    }) => {
      addSectionTitle(title, subtitle);

      if (body.length > 0) {
        autoTable(doc, {
          startY: cursorY,
          head: [head],
          body,
          theme: "striped",
          styles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
        });

        cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;
      } else {
        doc.setFontSize(10);
        doc.text(emptyMessage, 14, cursorY);
        cursorY += 8;
      }
    };

    // ----------------------------------------------------------------------
    // Section 1: Visits
    // ----------------------------------------------------------------------
    renderSectionTable({
      title: t("reports:income.realizedVisitsTitle"),
      subtitle: `${t("reports:common.subtotal")}: ${formatMoney(report.totals.visitsIncome)} ${currency}`,
      head: [t("reports:common.date"), t("reports:common.service"), t("reports:common.client"), t("reports:common.employee"), t("reports:common.amountWithCurrency", { currency })],
      body: report.visits.map((row) => [
        formatDateTimeCell(row.date),
        row.service || "—",
        row.client || "—",
        row.employee || "—",
        formatMoney(row.amount),
      ]),
      emptyMessage: t("reports:income.noRealizedVisits"),
    });

    // ----------------------------------------------------------------------
    // Section 2: Voucher sales
    // ----------------------------------------------------------------------
    renderSectionTable({
      title: t("reports:income.voucherSales"),
      subtitle: `${t("reports:common.subtotal")}: ${formatMoney(report.totals.voucherSalesIncome)} ${currency}`,
      head: [t("reports:common.date"), t("reports:common.voucherNo"), t("reports:common.buyer"), t("reports:common.recipient"), t("reports:common.amountWithCurrency", { currency })],
      body: report.voucherSales.map((row) => [
        formatDateTimeCell(row.date),
        row.voucherNo || "—",
        row.buyerName || "—",
        row.recipientName || "—",
        formatMoney(row.amount),
      ]),
      emptyMessage: t("reports:income.noVoucherSales"),
    });

    // ----------------------------------------------------------------------
    // Section 3: Package sales
    // ----------------------------------------------------------------------
    renderSectionTable({
      title: t("reports:income.packageSales"),
      subtitle: `${t("reports:common.subtotal")}: ${formatMoney(report.totals.packageSalesIncome)} ${currency}`,
      head: [t("reports:common.date"), t("reports:common.packageNo"), t("reports:common.buyer"), t("reports:common.recipient"), t("reports:common.amountWithCurrency", { currency })],
      body: report.packageSales.map((row) => [
        formatDateTimeCell(row.date),
        row.voucherNo || "—",
        row.buyerName || "—",
        row.recipientName || "—",
        formatMoney(row.amount),
      ]),
      emptyMessage: t("reports:income.noPackageSales"),
    });

    // ----------------------------------------------------------------------
    // Section 4: Voucher redeems correction
    // ----------------------------------------------------------------------
    renderSectionTable({
      title: t("reports:income.voucherRedeemsTitle"),
      subtitle: t("reports:common.subtractedFromIncome", { amount: formatMoney(report.totals.voucherRedeemsCorrection), currency }),
      head: [t("reports:common.date"), t("reports:common.voucherNo"), t("reports:common.reservationId"), t("reports:common.comment"), t("reports:common.redeemWithCurrency", { currency })],
      body: report.voucherRedeems.map((row) => [
        formatDateTimeCell(row.date),
        row.voucherNo || "—",
        String(row.reservationId || "—"),
        row.comment || "—",
        formatMoney(row.amount),
      ]),
      emptyMessage: t("reports:income.noVoucherRedeems"),
    });

    // ----------------------------------------------------------------------
    // Section 5: Package redeems correction
    // ----------------------------------------------------------------------
    renderSectionTable({
      title: t("reports:income.packageRedeemsTitle"),
      subtitle: t("reports:common.subtractedFromIncome", { amount: formatMoney(report.totals.packageRedeemsCorrection), currency }),
      head: [t("reports:common.date"), t("reports:common.packageNo"), t("reports:common.reservationId"), t("reports:common.comment"), t("reports:common.redeemWithCurrency", { currency })],
      body: report.packageRedeems.map((row) => [
        formatDateTimeCell(row.date),
        row.voucherNo || "—",
        String(row.reservationId || "—"),
        row.comment || "—",
        formatMoney(row.amount),
      ]),
      emptyMessage: t("reports:income.noPackageRedeems"),
    });

    // ----------------------------------------------------------------------
    // Final equation
    // ----------------------------------------------------------------------
    ensurePageBreak(24);

    doc.setFontSize(12);
    doc.text(t("reports:income.netResult"), 14, cursorY);

    autoTable(doc, {
      startY: cursorY + 4,
      head: [[t("reports:common.equation"), t("reports:common.value")]],
      body: [
        [
          `${formatMoney(report.totals.visitsIncome)} + ${formatMoney(
            report.totals.voucherSalesIncome
          )} + ${formatMoney(report.totals.packageSalesIncome)} - ${formatMoney(
            report.totals.voucherRedeemsCorrection
          )} - ${formatMoney(report.totals.packageRedeemsCorrection)}`,
          `${formatMoney(report.totals.netIncome)} ${currency}`,
        ],
      ],
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fontStyle: "bold" },
      margin: { left: 14, right: 14 },
    });

    doc.save(`Income_Report_${report.period.from}_${report.period.to}.pdf`);
  };

  return (
    <div className="income-report-page">
      {/* ------------------------------------------------------------------- */}
      {/* Page header                                                         */}
      {/* ------------------------------------------------------------------- */}
      <div className="income-report-header">
        <div className="income-header-left">
          <h1>{t("reports:income.title")}</h1>

          <p className="income-header-hint">
            {t("reports:income.businessRuleIntro")}
            <strong> {t("reports:income.businessRuleRealizedVisits")} </strong>
            + <strong> {t("reports:income.businessRuleVoucherSales")} </strong>
            + <strong> {t("reports:income.businessRulePackageSales")} </strong>
            - <strong> {t("reports:income.businessRuleVoucherRedeems")} </strong>
            - <strong> {t("reports:income.businessRulePackageRedeems")} </strong>
            = <strong> {t("reports:income.businessRuleNetIncome")}</strong>.
          </p>
        </div>
      </div>

      {error && <p className="income-error-message">{error}</p>}

      {/* ------------------------------------------------------------------- */}
      {/* Filters toolbar                                                     */}
      {/* ------------------------------------------------------------------- */}
      <div className="income-filters-bar">
        <div className="income-filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />
        </div>

        <div className="income-filters-actions">
          <div className="income-filter-item income-filter-item-pdf">
            <label className="income-filter-label">{t("reports:common.pdf")}</label>
            <select
              value={pdfOrientation}
              onChange={(e) => setPdfOrientation(e.target.value)}
              className="income-pdf-orientation"
            >
              <option value="p">{t("reports:common.portrait")}</option>
              <option value="l">{t("reports:common.landscape")}</option>
            </select>
          </div>

          <button
            className="income-generate-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>

          {canExportPdf && hasReportData && !loading && (
            <button className="income-pdf-button" onClick={generatePDF}>
              {t("reports:common.exportPdf")}
            </button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* Main local scroll area                                              */}
      {/* ------------------------------------------------------------------- */}
      <div className="income-report-scroll">
        {loading ? (
          <div className="income-loading-panel">{t("reports:common.loading")}</div>
        ) : !hasGeneratedOnce ? (
          <div className="income-empty-panel">
            {t("reports:common.selectPeriodAndGenerate")}
          </div>
        ) : !hasReportData ? (
          <div className="income-empty-panel">{t("reports:common.noDataAvailable")}</div>
        ) : (
          <>
            {/* -------------------------------------------------------------- */}
            {/* Sticky overview block                                         */}
            {/* -------------------------------------------------------------- */}
            <div className="income-sticky-overview">
              <div className="income-summary-grid">
                <div className="income-summary-card">
                  <span className="income-summary-label">{t("reports:income.visitsIncome")}</span>
                  <span className="income-summary-value">
                    {formatMoney(report.totals.visitsIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card">
                  <span className="income-summary-label">{t("reports:income.voucherSales")}</span>
                  <span className="income-summary-value">
                    {formatMoney(report.totals.voucherSalesIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card income-summary-card-package">
                  <span className="income-summary-label">{t("reports:income.packageSales")}</span>
                  <span className="income-summary-value">
                    {formatMoney(report.totals.packageSalesIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card income-summary-card-correction">
                  <span className="income-summary-label">{t("reports:income.voucherRedeemsCorrection")}</span>
                  <span className="income-summary-value income-summary-value-negative">
                    - {formatMoney(report.totals.voucherRedeemsCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card income-summary-card-package-correction">
                  <span className="income-summary-label">{t("reports:income.packageRedeemsCorrection")}</span>
                  <span className="income-summary-value income-summary-value-negative">
                    - {formatMoney(report.totals.packageRedeemsCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card">
                  <span className="income-summary-label">{t("reports:income.grossBeforeCorrection")}</span>
                  <span className="income-summary-value">
                    {formatMoney(report.totals.grossBeforeCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-summary-card income-summary-card-net">
                  <span className="income-summary-label">{t("reports:income.netIncome")}</span>
                  <span className="income-summary-value">
                    {formatMoney(report.totals.netIncome)} {report.currency}
                  </span>
                </div>
              </div>

              {/* Current equation is rendered ONCE here only */}
              <div className="income-formula-card">
                <div className="income-formula-title">{t("reports:income.currentEquation")}</div>
                <div className="income-formula-line">
                  <span>{formatMoney(report.totals.visitsIncome)}</span>
                  <span>+</span>
                  <span>{formatMoney(report.totals.voucherSalesIncome)}</span>
                  <span>+</span>
                  <span>{formatMoney(report.totals.packageSalesIncome)}</span>
                  <span>-</span>
                  <span>{formatMoney(report.totals.voucherRedeemsCorrection)}</span>
                  <span>-</span>
                  <span>{formatMoney(report.totals.packageRedeemsCorrection)}</span>
                  <span>=</span>
                  <strong>
                    {formatMoney(report.totals.netIncome)} {report.currency}
                  </strong>
                </div>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Section 1: Visits                                              */}
            {/* -------------------------------------------------------------- */}
            <div className="income-section-card">
              <div className="income-section-header">
                <div>
                  <div className="income-section-title">{t("reports:income.realizedVisitsTitle")}</div>
                  <div className="income-section-subtitle">
                    {t("reports:income.realizedVisitsSubtitle")}
                  </div>
                </div>

                <div className="income-section-metric">
                  <span className="income-section-metric-label">{t("reports:common.subtotal")}</span>
                  <span className="income-section-metric-value">
                    {formatMoney(report.totals.visitsIncome)} {report.currency}
                  </span>
                </div>
              </div>

              <div className="income-table-wrapper">
                <table className="income-data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:common.service")}</th>
                      <th>{t("reports:common.client")}</th>
                      <th>{t("reports:common.employee")}</th>
                      <th>{t("reports:common.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.visits.length > 0 ? (
                      report.visits.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTimeCell(row.date)}</td>
                          <td>{row.service}</td>
                          <td>{row.client}</td>
                          <td>{row.employee}</td>
                          <td>{formatMoney(row.amount)} {report.currency}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="income-empty-row">
                          {t("reports:income.noRealizedVisits")}
                        </td>
                      </tr>
                    )}

                    <tr className="income-total-row">
                      <td colSpan="4">
                        <strong>{t("reports:common.total")}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatMoney(report.totals.visitsIncome)} {report.currency}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Section 2: Voucher sales                                       */}
            {/* -------------------------------------------------------------- */}
            <div className="income-section-card">
              <div className="income-section-header">
                <div>
                  <div className="income-section-title">{t("reports:income.voucherSales")}</div>
                  <div className="income-section-subtitle">
                    {t("reports:income.voucherSalesSubtitle")}
                  </div>
                </div>

                <div className="income-section-metric">
                  <span className="income-section-metric-label">{t("reports:common.subtotal")}</span>
                  <span className="income-section-metric-value">
                    {formatMoney(report.totals.voucherSalesIncome)} {report.currency}
                  </span>
                </div>
              </div>

              <div className="income-table-wrapper">
                <table className="income-data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:common.voucherNo")}</th>
                      <th>{t("reports:common.buyer")}</th>
                      <th>{t("reports:common.recipient")}</th>
                      <th>{t("reports:common.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.voucherSales.length > 0 ? (
                      report.voucherSales.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTimeCell(row.date)}</td>
                          <td>{row.voucherNo}</td>
                          <td>{row.buyerName}</td>
                          <td>{row.recipientName}</td>
                          <td>{formatMoney(row.amount)} {report.currency}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="income-empty-row">
                          {t("reports:income.noVoucherSales")}
                        </td>
                      </tr>
                    )}

                    <tr className="income-total-row">
                      <td colSpan="4">
                        <strong>{t("reports:common.total")}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatMoney(report.totals.voucherSalesIncome)} {report.currency}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Section 3: Package sales                                       */}
            {/* -------------------------------------------------------------- */}
            <div className="income-section-card income-section-card-package">
              <div className="income-section-header">
                <div>
                  <div className="income-section-title">{t("reports:income.packageSales")}</div>
                  <div className="income-section-subtitle">
                    {t("reports:income.packageSalesSubtitle")}
                  </div>
                </div>

                <div className="income-section-metric">
                  <span className="income-section-metric-label">{t("reports:common.subtotal")}</span>
                  <span className="income-section-metric-value">
                    {formatMoney(report.totals.packageSalesIncome)} {report.currency}
                  </span>
                </div>
              </div>

              <div className="income-table-wrapper">
                <table className="income-data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:common.packageNo")}</th>
                      <th>{t("reports:common.buyer")}</th>
                      <th>{t("reports:common.recipient")}</th>
                      <th>{t("reports:common.amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.packageSales.length > 0 ? (
                      report.packageSales.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTimeCell(row.date)}</td>
                          <td>{row.voucherNo}</td>
                          <td>{row.buyerName}</td>
                          <td>{row.recipientName}</td>
                          <td>{formatMoney(row.amount)} {report.currency}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="income-empty-row">
                          {t("reports:income.noPackageSales")}
                        </td>
                      </tr>
                    )}

                    <tr className="income-total-row income-total-row-package">
                      <td colSpan="4">
                        <strong>{t("reports:common.total")}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatMoney(report.totals.packageSalesIncome)} {report.currency}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Section 4: Corrections / voucher redeems                       */}
            {/* -------------------------------------------------------------- */}
            <div className="income-section-card income-section-card-correction">
              <div className="income-section-header">
                <div>
                  <div className="income-section-title">{t("reports:income.voucherRedeemsTitle")}</div>
                  <div className="income-section-subtitle">
                    {t("reports:income.voucherRedeemsSubtitle")}
                  </div>
                </div>

                <div className="income-section-metric">
                  <span className="income-section-metric-label">{t("reports:common.subtracted")}</span>
                  <span className="income-section-metric-value income-summary-value-negative">
                    - {formatMoney(report.totals.voucherRedeemsCorrection)} {report.currency}
                  </span>
                </div>
              </div>

              <div className="income-table-wrapper">
                <table className="income-data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:common.voucherNo")}</th>
                      <th>{t("reports:common.reservationId")}</th>
                      <th>{t("reports:common.comment")}</th>
                      <th>{t("reports:common.redeem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.voucherRedeems.length > 0 ? (
                      report.voucherRedeems.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTimeCell(row.date)}</td>
                          <td>{row.voucherNo}</td>
                          <td>{String(row.reservationId)}</td>
                          <td>{row.comment}</td>
                          <td>{formatMoney(row.amount)} {report.currency}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="income-empty-row">
                          {t("reports:income.noVoucherRedeems")}
                        </td>
                      </tr>
                    )}

                    <tr className="income-total-row income-total-row-correction">
                      <td colSpan="4">
                        <strong>{t("reports:common.totalCorrection")}</strong>
                      </td>
                      <td>
                        <strong>
                          - {formatMoney(report.totals.voucherRedeemsCorrection)} {report.currency}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Section 5: Corrections / package redeems                       */}
            {/* -------------------------------------------------------------- */}
            <div className="income-section-card income-section-card-package-correction">
              <div className="income-section-header">
                <div>
                  <div className="income-section-title">{t("reports:income.packageRedeemsTitle")}</div>
                  <div className="income-section-subtitle">
                    {t("reports:income.packageRedeemsSubtitle")}
                  </div>
                </div>

                <div className="income-section-metric">
                  <span className="income-section-metric-label">{t("reports:common.subtracted")}</span>
                  <span className="income-section-metric-value income-summary-value-negative">
                    - {formatMoney(report.totals.packageRedeemsCorrection)} {report.currency}
                  </span>
                </div>
              </div>

              <div className="income-table-wrapper">
                <table className="income-data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:common.packageNo")}</th>
                      <th>{t("reports:common.reservationId")}</th>
                      <th>{t("reports:common.comment")}</th>
                      <th>{t("reports:common.redeem")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.packageRedeems.length > 0 ? (
                      report.packageRedeems.map((row) => (
                        <tr key={row.id}>
                          <td>{formatDateTimeCell(row.date)}</td>
                          <td>{row.voucherNo}</td>
                          <td>{String(row.reservationId)}</td>
                          <td>{row.comment}</td>
                          <td>{formatMoney(row.amount)} {report.currency}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="income-empty-row">
                          {t("reports:income.noPackageRedeems")}
                        </td>
                      </tr>
                    )}

                    <tr className="income-total-row income-total-row-package-correction">
                      <td colSpan="4">
                        <strong>{t("reports:common.totalCorrection")}</strong>
                      </td>
                      <td>
                        <strong>
                          - {formatMoney(report.totals.packageRedeemsCorrection)} {report.currency}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* -------------------------------------------------------------- */}
            {/* Final totals card                                              */}
            {/* -------------------------------------------------------------- */}
            <div className="income-grand-totals-card">
              <div className="income-grand-title">{t("reports:income.grandTotals")}</div>

              <div className="income-grand-grid">
                <div className="income-grand-metric">
                  <span className="income-grand-label">{t("reports:income.visitsIncome")}</span>
                  <span className="income-grand-value">
                    {formatMoney(report.totals.visitsIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric">
                  <span className="income-grand-label">{t("reports:income.voucherSales")}</span>
                  <span className="income-grand-value">
                    {formatMoney(report.totals.voucherSalesIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric income-grand-metric-package">
                  <span className="income-grand-label">{t("reports:income.packageSales")}</span>
                  <span className="income-grand-value">
                    {formatMoney(report.totals.packageSalesIncome)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric">
                  <span className="income-grand-label">{t("reports:income.voucherRedeemsCorrection")}</span>
                  <span className="income-grand-value income-summary-value-negative">
                    - {formatMoney(report.totals.voucherRedeemsCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric income-grand-metric-package-correction">
                  <span className="income-grand-label">{t("reports:income.packageRedeemsCorrection")}</span>
                  <span className="income-grand-value income-summary-value-negative">
                    - {formatMoney(report.totals.packageRedeemsCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric">
                  <span className="income-grand-label">{t("reports:income.grossBeforeCorrection")}</span>
                  <span className="income-grand-value">
                    {formatMoney(report.totals.grossBeforeCorrection)} {report.currency}
                  </span>
                </div>

                <div className="income-grand-metric income-grand-metric-net">
                  <span className="income-grand-label">{t("reports:income.netIncome")}</span>
                  <span className="income-grand-value">
                    {formatMoney(report.totals.netIncome)} {report.currency}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IncomeReportPage;